import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiltersBar, Icon, InfiniteScrollingObserver, PeriodFilter, SearchInput, SingleSelect } from '~/components';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import { intervalOptions, intervalsByScope } from '~/utils';
import humanize from '../utils/humanize';
import {
  EmptyState,
  EntityDetail,
  EntityIcon,
  EntityImage,
  EntityInfo,
  EntityName,
  EntityRow,
  Table,
  Td,
  Th,
} from '../ai/replay/shared';
import EntitySearchFilter from '../ai/replay/EntitySearchFilter';
import MemberDisplay from '../ai/replay/MemberDisplay';
import WorkspaceDisplay from '../ai/replay/WorkspaceDisplay';
import ActorCell from '../traces/ActorCell';

const BuildingIcon = styled(Icon).attrs({ icon: 'building' })`
  font-size: 1rem;
  color: ${colors.grey25};
`;

const BuildingIconSmall = styled(Icon).attrs({ icon: 'building' })`
  font-size: 0.875rem;
  color: ${colors.grey25};
`;

const SelectedLabel = styled.span`
  margin-left: 0.375rem;
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover td {
    background: ${colors.grey5};
  }
`;

// See TraceList: FiltersBar carries flex: 1 for its usual flex-row home; in the
// admin Page's flex column it must reset to content height (flex: none) and pack
// its rows to the top, or the two rows stretch apart on a short result set.
const Filters = styled(FiltersBar)`
  grid-template-columns: repeat(4, minmax(0, 1fr));
  flex: none;
  align-content: start;
  margin-bottom: 1.5rem;
`;

const SearchSpan = styled.div`
  grid-column: span 2;
`;

// The channel a notification went out on — the bare set the engine records.
const CHANNELS = ['email', 'slack', 'microsoft_teams'];

const CHANNEL_COLORS = {
  email: colors.primary,
  slack: colors.accent,
  microsoft_teams: colors.purple100,
};

const ChannelBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $channel }) => CHANNEL_COLORS[$channel] || colors.grey40};
`;

const StatusBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $delivered }) => ($delivered ? colors.success : colors.danger)};
`;

// The recipient when it didn't resolve to a member (an external client address, a
// Slack channel id) — plain text matching the table body.
const Recipient = styled.div`
  font-size: 0.875rem;
  color: ${colors.black};
`;

// The message subject / text preview under the recipient.
const Summary = styled.div`
  margin-top: 0.125rem;
  font-size: 0.75rem;
  color: ${colors.grey55};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 22rem;
`;

function renderWorkspaceItem(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.5rem">
        {item.imageUrl ? <EntityImage src={item.imageUrl} alt={item.label} /> : <BuildingIcon />}
      </EntityIcon>
      <EntityInfo>
        <EntityName>{item.label}</EntityName>
        {item.key && <EntityDetail>{item.key}</EntityDetail>}
      </EntityInfo>
    </EntityRow>
  );
}

function renderWorkspaceSelected(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.125rem">
        {item.imageUrl ? <EntityImage src={item.imageUrl} alt={item.label} /> : <BuildingIconSmall />}
      </EntityIcon>
      <SelectedLabel>{item.label}</SelectedLabel>
    </EntityRow>
  );
}

export default function NotificationList() {
  const api = useApi();
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const [period, setPeriod] = useState(() => {
    const start = urlParams.get('start') || undefined;
    const end = urlParams.get('end') || undefined;
    return start || end ? { key: intervalOptions.custom.key, start, end } : intervalOptions.all_dates;
  });
  const [channel, setChannel] = useState(urlParams.get('channel') || '');
  const [searchText, setSearchText] = useState(urlParams.get('q') || '');
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const start = period.start || undefined;
  const end = period.end || undefined;

  // Resolve the workspace from the URL param on mount, so the filter chip rehydrates
  // when the page is opened with ?workspaceId= (e.g. a back link from the detail).
  const initialWorkspaceId = useRef(urlParams.get('workspaceId'));

  useEffect(() => {
    const wId = initialWorkspaceId.current;
    if (!wId) return;
    api.admin.notificationReplay.resolve({ workspaceId: wId }).then(({ data }) => {
      if (data.workspace) {
        setSelectedWorkspace({
          id: data.workspace.id,
          label: data.workspace.name,
          key: data.workspace.key,
          imageUrl: data.workspace.imageUrl,
        });
      }
    });
  }, [api]);

  // Sync filters to URL, and stash the query so NotificationDetail's back link can
  // restore the exact filters the user left.
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedWorkspace) params.set('workspaceId', selectedWorkspace.id);
    if (channel) params.set('channel', channel);
    if (searchText) params.set('q', searchText);
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    const search = qs ? `?${qs}` : '';
    sessionStorage.setItem('admin_notifications_search', search);
    history.replace({ search });
  }, [selectedWorkspace, channel, searchText, start, end, history]);

  const searchDebounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState(urlParams.get('q') || '');

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchText]);

  const loadMore = useCallback(async () => {
    const params = { from: notifications.length };
    if (start) params.start = start;
    if (end) params.end = end;
    if (channel) params.channel = channel;
    if (debouncedSearch) params.q = debouncedSearch;
    if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;

    const { data } = await api.admin.notificationReplay.list(params);
    setNotifications((prev) => [...prev, ...data.notifications]);
    setHasMore(data.hasMore);
  }, [api, start, end, channel, debouncedSearch, selectedWorkspace, notifications.length]);

  useEffect(() => {
    let cancelled = false;
    setNotifications([]);
    setHasMore(false);
    setIsLoading(true);

    const fetch = async () => {
      try {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        if (channel) params.channel = channel;
        if (debouncedSearch) params.q = debouncedSearch;
        if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;

        const { data } = await api.admin.notificationReplay.list(params);
        if (!cancelled) {
          setNotifications(data.notifications);
          setHasMore(data.hasMore);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, channel, debouncedSearch, selectedWorkspace]);

  const searchWorkspaces = useCallback(
    async (text, from) => {
      const params = {};
      if (text) params.q = text;
      if (from) params.from = from;
      const { data } = await api.admin.notificationReplay.searchWorkspaces(params);
      return {
        items: data.results.map((w) => ({ id: w.id, label: w.name, key: w.key, imageUrl: w.imageUrl })),
        hasMore: data.hasMore,
      };
    },
    [api],
  );

  const openWorkspaces = useCallback(
    async (text, from) => {
      const params = {};
      if (from) params.from = from;
      const { data } = await api.admin.notificationReplay.recentWorkspaces(params);
      return {
        items: data.results.map((w) => ({ id: w.id, label: w.name, key: w.key, imageUrl: w.imageUrl })),
        hasMore: data.hasMore,
      };
    },
    [api],
  );

  return (
    <>
      <Filters>
        <SearchSpan>
          <SearchInput
            value={searchText}
            placeholder="Search recipient or message"
            onChange={({ target }) => setSearchText(target.value)}
          />
        </SearchSpan>
        <EntitySearchFilter
          placeholder="Workspace"
          selected={selectedWorkspace}
          onSelect={(w) => setSelectedWorkspace(w)}
          onClear={() => setSelectedWorkspace(null)}
          searchFn={searchWorkspaces}
          onOpen={openWorkspaces}
          renderItem={renderWorkspaceItem}
          renderSelected={renderWorkspaceSelected}
        />
        <SingleSelect
          placeholder="Channel"
          value={channel}
          showEmptyOption
          onChange={(e) => setChannel(e.target.value)}>
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {humanize(c)}
            </option>
          ))}
        </SingleSelect>
        <PeriodFilter
          name="period"
          placeholder="Period"
          scope="day"
          intervals={[intervalOptions.all_dates, ...intervalsByScope.day]}
          value={period}
          onChange={({ target: { value } }) => setPeriod(value)}
        />
      </Filters>

      {isLoading && <EmptyState>Loading notifications...</EmptyState>}

      {!isLoading && notifications.length === 0 && <EmptyState>No notifications found.</EmptyState>}

      {!isLoading && notifications.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Channel</Th>
                <Th>Recipient</Th>
                <Th>Workspace</Th>
                <Th>Triggered by</Th>
                <Th>Status</Th>
                <Th>Sent</Th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((n) => (
                <ClickableRow key={n.id} onClick={() => history.push(`/admin/notifications/${n.id}`)}>
                  <Td>
                    <ChannelBadge $channel={n.channel}>{n.channel}</ChannelBadge>
                  </Td>
                  <Td>
                    {n.recipientMember ? (
                      <MemberDisplay member={n.recipientMember} />
                    ) : (
                      <Recipient>{n.recipient || '-'}</Recipient>
                    )}
                    {n.summary && <Summary title={n.summary}>{n.summary}</Summary>}
                  </Td>
                  <Td>
                    <WorkspaceDisplay workspace={n.workspace} />
                  </Td>
                  <Td>
                    <ActorCell actor={n.actor} member={n.member} />
                  </Td>
                  <Td>
                    <StatusBadge $delivered={n.delivered}>{n.delivered ? 'delivered' : 'failed'}</StatusBadge>
                  </Td>
                  <Td title={n.occurredAt}>{moment(n.occurredAt).fromNow()}</Td>
                </ClickableRow>
              ))}
            </tbody>
          </Table>

          {hasMore && <InfiniteScrollingObserver key={notifications.length} onIntersecting={loadMore} />}
        </>
      )}
    </>
  );
}
